import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useForm } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';

import OnOffByScroll from '../../../Animation/onOffByScroll';
import { AuthContext } from '../../../auth';
import various from '../../../General/various';
import Footer from '../../../Footer/footer';
import { tagCreate } from '../../../Apis/apiOfManaged';
import BreadCrumbs from '../../../General/breadCrumbs';
import { Spinner } from '../../../Spinner';

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    margin: '0px 0px 80px 0px',
    display: 'flex',
    flexDirection: 'column',
    '& > div': {
      textAlign: 'start',
      '& > span': {
        display: 'inline-block',
        marginRight: '24px',
        fontSize: '0.9rem',
      },
      '& #title': {
        fontWeight: 'bold',
        fontSize: theme.typography.fontSize,
      },
    },
  },
  titles: {},
  inputs: {},
  helptext: {},
  error: {
    color: 'red',
    fontSize: '0.7em',
  },
  container: {
    marginTop: '80px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '@media screen and (max-width:800px)': {
      '& > div': {
        width: '100%',
      },
    },
    '& > form': {
      '& > div': {
        width: '70%',
        display: 'flex',
        margin: '0 auto',
        marginBottom: '32px',

        '& > div': {
          textAlign: 'start',
          display: 'flex',
          justifyContent: 'start',
          '& > img': {
            maxWidth: '300px',
            maxHeight: '300px',
            objectFit: 'contain',
          },
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
        },

        '& $titles': {
          width: '30%',
          marginRight: '32px',
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
          '& > label': {
            width: '100%',
            textAlign: 'end',
            fontSize: '1rem',
            '@media screen and (max-width:750px)': {
              textAlign: 'start',
              marginBottom: '16px',
            },
          },
          '& > span': {
            color: 'red',
          },
        },

        '& $inputs': {
          width: '70%',
          flexWrap: 'wrap',
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
          '& > input, & > textarea, & > select': {
            width: '100%',
            height: '40px',
            padding: '8px',
            fontFamily: theme.typography.fontFamily,
            border: '0.5px solid grey',
            borderRadius: '0.5em',
            boxShadow: '0px 0px 15px 0px rgba(0,0,0,0.1) inset',
            '&:focus': {
              outline: `1.2px ${theme.palette.primary.main} outset`,
            },
          },
          '& > textarea': {
            minHeight: '160px',
          },
          '& > input[type="checkbox"]': {
            boxShadow: 'none',
          },
          '& $helptext': {
            fontSize: '0.8rem',
            color: 'grey',
            '& > a': {
              textDecoration: 'underline',
              '&:link': {
                color: theme.palette.primary.main,
              },
            },
          },
        },

        '@media screen and (max-width:750px)': {
          flexWrap: 'wrap',
        },
      },
    },
  },
  button: {
    marginTop: '88px !important',
    justifyContent: 'end',
    '& > button': {
      display: 'block',
      fontFamily: theme.typography.fontFamily,
      fontWeight: 'bold',
      borderRadius: '0.5em',
      border: 'none',
      padding: '4px 8px',
      backgroundColor: theme.palette.primary.main,
      boxShadow: '0px 0px 15px rgba(0,0,0,0.1)',
      color: '#fff',
      cursor: 'pointer',
    },
  },
}));

const TagCreate = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {},
  });

  const { authReady, isAuth, permissions } = useContext(AuthContext);
  const hasAdminPermission =
    Array.isArray(permissions) && permissions.includes('admin');

  const [date, setDate] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = location.state?.previous || various.general.tagIndex;

  const CRUMBS = [
    { name: '管理画面Top', path: '/manage/index' },
    { name: 'タグの登録', path: '/manage/tag_create' },
  ];

  // Handler
  const handleAuth = () => {
    navigate(various.general.login, { state: { previous: location.pathname } });
  };

  // Init
  useEffect(() => {
    const weekday = ['日', '月', '火', '水', '木', '金', '土'];
    const d = new Date();
    setDate(
      d.getFullYear() +
        '年' +
        (d.getMonth() + 1) +
        '月' +
        d.getDate() +
        '日' +
        '（' +
        weekday[d.getDay()] +
        '）'
    );
  }, []);

  // Submit
  const onSubmit = handleSubmit(async (data) => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await tagCreate(data);
      navigate(returnPath, {
        state: {
          previous: location.pathname,
          uuid: location.state?.uuid,
        },
      });
    } catch (error) {
      setSubmitError('タグの登録に失敗しました。入力内容とログイン状態をご確認ください。');
    } finally {
      setIsSubmitting(false);
    }
  });

  // Contents
  const form = () => {
    return (
      <>
        <form method="POST" encType="multipart/form-data" onSubmit={onSubmit}>
          <div>
            <div className={classes.titles}>
              <label htmlFor="Tagネーム">Tagネーム</label>
              <span> *</span>
            </div>
            <div className={classes.inputs}>
              <input
                id="name"
                type="text"
                {...register('name', {
                  required: { value: true, message: '入力必須の項目です' },
                })}
              />
              {errors.name?.message && (
                <div className={classes.error}>{errors.name.message}</div>
              )}
            </div>
          </div>
          <div>
            <div className={classes.titles}>
              <label htmlFor="運用区分">{'運用区分'}</label>
            </div>
            <div className={classes.inputs}>
              <input
                id="is_useful"
                type="checkbox"
                defaultChecked={true}
                {...register('is_useful', {
                  required: { value: false, message: '入力必須の項目です' },
                })}
              />
              {errors.is_useful?.message && (
                <div className={classes.error}>{errors.is_useful.message}</div>
              )}
            </div>
          </div>
          <div>
            <div className={classes.titles}>
              <label htmlFor="タグの説明">タグの説明</label>
              <span> *</span>
            </div>
            <div className={classes.inputs}>
              <textarea
                id="description"
                type="textarea"
                {...register('description', {
                  required: { value: true, message: '入力必須の項目です' },
                  maxLength: {
                    value: 150,
                    message: '150字以内でお願いします',
                  },
                })}
              />
              <div className={classes.helptext}>
                150文字以内で記述してください。
              </div>
              {errors.description?.message && (
                <div className={classes.error}>
                  {errors.description.message}
                </div>
              )}
            </div>
          </div>
          <div className={classes.button}>
            <button type="submit" disabled={isSubmitting}>
              登録
            </button>
          </div>
          {isSubmitting && <Spinner />}
          {submitError && <div className={classes.error}>{submitError}</div>}
        </form>
      </>
    );
  };

  return (
    <>
      <div style={{ margin: '80px' }}>
        <OnOffByScroll place="General" />
      </div>
      {!authReady ? (
        <Spinner />
      ) : !isAuth ? (
        <>
          <div>
            <span
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleAuth}
            >
              ログイン
            </span>
            してください。
          </div>
          <Footer />
        </>
      ) : hasAdminPermission ? (
        <>
          <div className={classes.pageTitle}>
            <div>
              <span id="title">タグの登録</span>
              <span>{date}</span>
            </div>
          </div>
          <hr />
          <BreadCrumbs crumbs={CRUMBS} />
          <div className={classes.container}>{form()}</div>
          <Footer />
        </>
      ) : (
        <>
          <div className={classes.pageTitle}>
            <div>
              <span id="title">タグの登録</span>
              <span>{date}</span>
            </div>
          </div>
          <hr />
          <BreadCrumbs crumbs={CRUMBS} />
          <div className={classes.container}>権限がありません</div>
        </>
      )}
    </>
  );
};
export default TagCreate;
